export type AppRoute =
  | { kind: "home" }
  | { kind: "health" }
  | { kind: "ui-demo" }
  | { kind: "organizer-sign-in" }
  | { kind: "my-quizzes" }
  | { kind: "organizer-wizard" }
  | { kind: "organizer-quiz-detail"; quizId: string }
  | {
      kind: "organizer-quiz-transcript";
      quizId: string;
      assignmentId: string;
    }
  | { kind: "participant-quiz"; quizId: string };

const QUIZ_VIEW_PREFIX = "/quizzes/view/";
const QUIZ_INVITE_PREFIX = "/quiz/";

const UUID_TAIL =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

/**
 * Parses **`/quizzes/view/{uuid}`** from a normalized pathname.
 *
 * @param pathname - Path without stray trailing slash except root.
 */
export function parseViewQuizId(pathname: string): string | undefined {
  if (!pathname.startsWith(QUIZ_VIEW_PREFIX)) {
    return undefined;
  }
  const rest = pathname.slice(QUIZ_VIEW_PREFIX.length);
  if (!UUID_TAIL.test(rest)) {
    return undefined;
  }
  return rest;
}

/**
 * Parses **`/quizzes/view/{quizId}/transcript/{assignmentId}`** from a normalized pathname.
 */
export function parseOrganizerTranscriptPath(pathname: string):
  | {
      quizId: string;
      assignmentId: string;
    }
  | undefined {
  if (!pathname.startsWith(QUIZ_VIEW_PREFIX)) {
    return undefined;
  }
  const rest = pathname.slice(QUIZ_VIEW_PREFIX.length);
  const parts = rest.split("/");
  if (parts.length !== 3) {
    return undefined;
  }
  const [quizId, segment, assignmentId] = parts;
  if (segment !== "transcript") {
    return undefined;
  }
  if (quizId === undefined || assignmentId === undefined) {
    return undefined;
  }
  if (!UUID_TAIL.test(quizId) || !UUID_TAIL.test(assignmentId)) {
    return undefined;
  }
  return { quizId, assignmentId };
}

/**
 * Parses **`/quiz/{uuid}`** (invitation email link) from a normalized pathname.
 *
 * @param pathname - Path without stray trailing slash except root.
 */
export function parseParticipantQuizId(pathname: string): string | undefined {
  if (!pathname.startsWith(QUIZ_INVITE_PREFIX)) {
    return undefined;
  }
  const rest = pathname.slice(QUIZ_INVITE_PREFIX.length);
  if (!UUID_TAIL.test(rest)) {
    return undefined;
  }
  return rest;
}

/**
 * Maps browser pathname (History API) to an application route.
 *
 * @param pathname - Typically `location.pathname` without query string.
 */
export function matchRoute(pathname: string): AppRoute {
  const normalized =
    pathname.endsWith("/") && pathname.length > 1
      ? pathname.slice(0, -1)
      : pathname;
  if (normalized === "/health") {
    return { kind: "health" };
  }
  if (normalized === "/demo/ui") {
    return { kind: "ui-demo" };
  }
  if (normalized === "/organizer/sign-in") {
    return { kind: "organizer-sign-in" };
  }
  if (normalized === "/quizzes") {
    return { kind: "my-quizzes" };
  }
  if (normalized.startsWith(QUIZ_VIEW_PREFIX)) {
    const transcript = parseOrganizerTranscriptPath(normalized);
    if (transcript !== undefined) {
      return {
        kind: "organizer-quiz-transcript",
        quizId: transcript.quizId,
        assignmentId: transcript.assignmentId,
      };
    }
    const quizId = parseViewQuizId(normalized);
    if (quizId !== undefined) {
      return { kind: "organizer-quiz-detail", quizId };
    }
  }
  if (
    normalized === "/quizzes/wizard" ||
    normalized.startsWith("/quizzes/wizard/")
  ) {
    return { kind: "organizer-wizard" };
  }
  const inviteId = parseParticipantQuizId(normalized);
  if (inviteId !== undefined) {
    return { kind: "participant-quiz", quizId: inviteId };
  }
  return { kind: "home" };
}
